import { Component, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatTableDataSource } from '@angular/material/table';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { filter, Observable } from 'rxjs';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';
import { ApexAxisChartSeries, ApexDataLabels, ApexYAxis } from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    GrdfAccessRightModel,
    GrdfConsumptionModel,
    GrdfConsumptionStep,
    GrdfConsumptionStepLabel,
} from '../../../grdf.models';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { GrdfState } from '../../../grdf.state';
import { GrdfService } from '../../../grdf.service';
import { AuthState } from 'src/app/core/states/auth.state';
import { User } from 'src/app/core/models/user.model';

interface ConsumptionsTableElement extends Partial<GrdfConsumptionModel> {}

const consumptionsDisplayedColumnsMinimum: DisplayedColumns[] = [
    {
        displayType: DisplayType.DATETIME,
        objectDisplayName: 'Début',
        objectKey: 'dateStart',
        bold: false,
    },
    {
        displayType: DisplayType.DATETIME,
        objectDisplayName: 'Fin',
        objectKey: 'dateEnd',
        bold: false,
    },
    {
        displayType: DisplayType.TEXT,
        objectDisplayName: 'Statut',
        objectKey: 'typeConsumption',
        bold: false,
    },
    {
        displayType: DisplayType.NUMBER,
        objectDisplayName: 'Consommation (kWh)',
        objectKey: 'consumptionkWh',
        bold: false,
    },
];

const consumptionsDisplayedColumnsRaw: DisplayedColumns[] = [
    ...consumptionsDisplayedColumnsMinimum,
    {
        displayType: DisplayType.TEXT,
        objectDisplayName: 'Volume brut (m3)',
        objectKey: 'rawVolume',
        bold: false,
    },
    {
        displayType: DisplayType.NUMBER,
        objectDisplayName: 'Facteur de conversion (kWh PCS / m3)',
        objectKey: 'calcConversionCoeff',
        bold: false,
    },
    {
        displayType: DisplayType.TEXT,
        objectDisplayName: 'Sens du flux',
        objectKey: 'gasFlowDirection',
        bold: false,
    },
    {
        displayType: DisplayType.DATE,
        objectDisplayName: 'Journée gaziere',
        objectKey: 'gasDay',
        bold: false,
    },
    {
        displayType: DisplayType.NUMBER,
        objectDisplayName: 'Qualification',
        objectKey: 'consumptionQualificationType',
        bold: false,
    },
    {
        displayType: DisplayType.TEXT,
        objectDisplayName: 'Statut',
        objectKey: 'consumptionStatus',
        bold: false,
    },
];

@UntilDestroy()
@Component({
    selector: 'app-grdf-meter-consumption',
    templateUrl: './grdf-meter-consumption.component.html',
    styleUrls: ['./grdf-meter-consumption.component.scss'],
})
export class GrdfMeterConsumptionComponent {
    public grdfAccessRight$: Observable<GrdfAccessRightModel> = inject(Store)
        .select(GrdfState.currentAccessRight)
        .pipe(filter(Boolean));
    public accessRight: GrdfAccessRightModel | null = null;

    public forceReload$: Observable<boolean> = inject(Store)
        .select(GrdfState.forceReload)
        .pipe(filter(Boolean));

    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public loggedUser: User | undefined;

    private now = new Date();
    dateStart: Date = new Date(this.now.getFullYear(), 0, this.now.getDate());
    dateEnd: Date = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());
    step: GrdfConsumptionStep = GrdfConsumptionStep.MONTHLY;
    forceReload: boolean = false;

    consumptions: ApexAxisChartSeries = [];
    loading = signal(false);
    allowReload = signal(false);

    stepOptions = Object.entries(GrdfConsumptionStepLabel).map(([k, v]) => ({
        value: k,
        label: v,
    }));

    consumptionsChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    consumptionsChartYaxis: ApexYAxis = {
        min: 0,
        title: { text: 'Consommation (kWh)' },
        labels: {
            formatter: (value: number) => convertNumber(value),
        },
    };
    buttonColorsSecondary = ButtonColors.Secondary;
    buttonColorsPrimary = ButtonColors.Primary;

    public consumptionsDataSource: MatTableDataSource<ConsumptionsTableElement> =
        new MatTableDataSource<ConsumptionsTableElement>();
    public consumptionsDisplayedColumns: DisplayedColumns[] = consumptionsDisplayedColumnsMinimum;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly grdfService: GrdfService,
        private readonly alertService: AlertService,
        private readonly store: Store,
    ) {
        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
        });

        this.forceReload$.pipe(untilDestroyed(this)).subscribe((forceReload: boolean) => {
            this.forceReload = forceReload;
            if (this.loggedUser?.isAdmin && forceReload) {
                this.fetchData();
            }
        });
        this.grdfAccessRight$.pipe(untilDestroyed(this)).subscribe((grdfAccessRight) => {
            this.accessRight = grdfAccessRight;
            this.fetchData();
        });
    }

    async updateStep(step: GrdfConsumptionStep) {
        if (step === GrdfConsumptionStep.DAILY || step === GrdfConsumptionStep.INFORMATIVE) {
            const oneMonthRecommended = new Date(this.dateEnd);
            oneMonthRecommended.setMonth(oneMonthRecommended.getMonth() - 1);
            this.dateStart = new Date(
                Math.max(this.dateStart.getTime(), oneMonthRecommended.getTime()),
            );
        }
        this.step = step;
        this.allowReload.set(true);
    }

    async updateDateStart(dateStart: Date) {
        this.dateStart = dateStart;
        this.allowReload.set(true);
    }
    async updateDateEnd(dateEnd: Date) {
        this.dateEnd = new Date(Math.min(dateEnd.getTime(), new Date().getTime()));
        this.allowReload.set(true);
    }

    getSerieName(detail: GrdfConsumptionModel): string {
        let result = `${detail.typeConsumption}`;
        if (detail.consumptionQualificationType) {
            result = `${detail.consumptionQualificationType} - ${result}`;
        }
        if (detail.gasFlowDirection) {
            result = `${detail.gasFlowDirection} - ${result}`;
        }
        return result;
    }

    getSeriesName(entities: GrdfConsumptionModel[]): string[] {
        const set = new Set<string>();

        for (const entity of entities) {
            set.add(this.getSerieName(entity));
        }

        return Array.from(set);
    }

    async fetchData(): Promise<void> {
        if (!this.dateStart || !this.dateEnd || !this.accessRight) return;
        if (this.dateStart.getTime() >= this.dateEnd.getTime()) {
            this.alertService.warning(
                'Dates invalides',
                `La date de début doit être inférieure à la date de fin`,
            );
            return;
        }

        this.loading.set(true);
        this.consumptions = [];
        if (this.accessRight) {
            try {
                const data: GrdfConsumptionModel[] = await this.grdfService.getConsumptions(
                    this.accessRight.id,
                    this.dateStart,
                    this.dateEnd,
                    this.step,
                    this.forceReload,
                );
                if (data.length == 0) {
                    this.alertService.warning('Consommation', 'Aucune donnée sur cette periode');
                    this.loading.set(false);
                    return;
                }

                if (
                    [GrdfConsumptionStep.INFORMATIVE, GrdfConsumptionStep.PUBLISHED].includes(
                        this.step,
                    )
                )
                    this.consumptionsDisplayedColumns = consumptionsDisplayedColumnsRaw;
                else this.consumptionsDisplayedColumns = consumptionsDisplayedColumnsMinimum;

                const seriesName = this.getSeriesName(data);
                this.consumptions = seriesName.map((serieName) => ({
                    name: serieName,
                    type: 'area',
                    data: data
                        .filter((d) => this.getSerieName(d) === serieName)
                        .map((d) => ({
                            x: d.dateStart,
                            y: d.consumptionkWh,
                        })),
                }));
                this.consumptionsChartDataLabels.enabledOnSeries = [];

                this.fillTable(data);

                this.allowReload.set(false);
                this.loading.set(false);
            } catch (error: any) {
                console.log(error);
                this.consumptions = [];
                this.loading.set(false);
                this.alertService.error(
                    'Erreur lors de la récupération des consommations',
                    `${error?.error?.message ?? 'Une erreur est survenue'}`,
                );
            }
        }
    }

    fillTable(data: GrdfConsumptionModel[]) {
        this.consumptionsDataSource = new MatTableDataSource<ConsumptionsTableElement>(data);
    }
}
